<template>
    <section>
        <div class="content mt-6 mb-6" v-if="!ready">
            <h5 class="has-text-grey"><i class="fal fa-tennis-ball fa-spin mr-3"></i>Loading. Please wait...</h5>
        </div>

        <div class="content" v-else>
            <h3 class="mt-2 mb-5 ml-2 is-hidden-desktop"><i class="fal fa-calendar-day fa-fw mr-3"></i>Today</h3>

            <today-pre-trip
                v-if="tripPhase === 'pre'"
                :trip="trip"
                :registration="registration"
                :user="user"
                :has-payment-plan="hasPaymentPlan"
                :announcements="announcementsSlice"
                :latest-poll="latestPoll"
                :experience-enabled="experienceEnabled"
                :is-staff="isStaff"
                @changed="loadAnnouncements"
            />

            <today-post-trip
                v-else-if="tripPhase === 'post'"
                :trip="trip"
                :registration="registration"
                :announcements="announcementsSlice"
                :latest-poll="latestPoll"
                :media="media"
                :participants="participants"
                :experience-enabled="experienceEnabled"
                :is-staff="isStaff"
                @changed="loadAnnouncements"
            />

            <today-active
                v-else
                :trip="trip"
                :registration="registration"
                :itinerary="itinerary"
                :feed="feed"
                :media="media"
                :announcements="announcementsSlice"
                :latest-poll="latestPoll"
                :experience-enabled="experienceEnabled"
                :is-staff="isStaff"
                @changed="loadAnnouncements"
            />
        </div>
    </section>
</template>

<script>
    import {mapGetters} from 'vuex';
    import {client as http} from '@/http_client';
    import * as socket from '@/socket';
    import TodayPreTrip from './TodayPreTrip.vue';
    import TodayActive from './TodayActive.vue';
    import TodayPostTrip from './TodayPostTrip.vue';

    // Only recent announcements appear on Today — they sit at the top of the
    // page, so anything shown there must be worth relaying right now. Wider
    // than the Experience rail's 12-hour window (Today is glanced at daily);
    // when nothing falls inside it the whole block disappears.
    const ANNOUNCEMENTS_FETCH = 10;
    const ANNOUNCEMENTS_SHOWN = 5;
    const ANNOUNCEMENTS_WINDOW_HOURS = 48;

    export default {
        name: 'Today',
        components: {TodayPreTrip, TodayActive, TodayPostTrip},
        data: function() {
            return {
                user: {},
                billing: {},
                itinerary: [],
                announcements: [],
                feed: [],
                media: [],
                participants: []
            };
        },
        computed: {
            ...mapGetters(['currentUser', 'trip', 'registration', 'tripPhase']),
            ready: function() {
                return !!(this.trip.id && this.registration.id);
            },
            experienceEnabled: function() {
                return this.trip.app_experience === 1;
            },
            isStaff: function() {
                return this.registration.type === 'staff' || this.registration.second_type === 'staff';
            },
            // GET /billing returns {} when no payment plan exists.
            hasPaymentPlan: function() {
                return !!this.billing.id;
            },
            recentAnnouncements: function() {
                const cutoff = Date.now() - ANNOUNCEMENTS_WINDOW_HOURS * 3600 * 1000;
                return this.announcements.filter(p => new Date(p.created_at).getTime() >= cutoff);
            },
            announcementsSlice: function() {
                return this.recentAnnouncements.slice(0, ANNOUNCEMENTS_SHOWN);
            },
            // The most recent still-current poll — passed separately only when
            // it isn't already visible in the slice.
            latestPoll: function() {
                const poll = this.recentAnnouncements.find(p => p.options && p.options.length);
                if (!poll) return null;
                if (this.announcementsSlice.some(p => p.id === poll.id)) return null;
                return poll;
            }
        },
        methods: {
            loadAll: function() {
                if (!this.ready) return;
                this.loadUser();
                this.loadAnnouncements();
                if (this.tripPhase === 'pre') this.loadBilling();
                if (this.tripPhase === 'active' && this.trip.itinerary_visable) this.loadItinerary();
                if (this.tripPhase === 'active') this.loadFeed();
                if (this.tripPhase === 'active' || this.tripPhase === 'post') this.loadMedia();
                if (this.tripPhase === 'post') this.loadParticipants();
            },
            loadUser: function() {
                http.get('/app/user', {force: true}).then(response => {
                    this.user = response.data;
                });
            },
            loadBilling: function() {
                http.get(`/app/registrations/${this.registration.id}/billing`, {force: true}).then(response => {
                    this.billing = response.data;
                });
            },
            loadItinerary: function() {
                http.get(`/app/registrations/${this.registration.id}/itinerary`, {force: true}).then(response => {
                    this.itinerary = response.data;
                });
            },
            loadAnnouncements: function() {
                if (!this.experienceEnabled) return;
                http.get(`/app/experience/${this.registration.id}/announcements?limit=${ANNOUNCEMENTS_FETCH}`, {force: true}).then(response => {
                    this.announcements = response.data.posts;
                });
            },
            loadMedia: function() {
                if (!this.experienceEnabled) return;
                http.get(`/app/experience/${this.registration.id}/media?limit=12`, {force: true}).then(response => {
                    this.media = response.data.media;
                });
            },
            loadParticipants: function() {
                http.get('/app/trips/' + this.registration.trip_id + '/participants', {force: true}).then(response => {
                    this.participants = response.data;
                });
            },
            // Compact rail teaser during the trip — both channels, newest first.
            loadFeed: function() {
                if (!this.experienceEnabled) return;
                http.get(`/app/experience/${this.registration.id}/feed?limit=5`, {force: true}).then(response => {
                    this.feed = response.data.posts;
                });
            },
            onPostNew: function() {
                this.loadAnnouncements();
                if (this.tripPhase === 'active') {
                    this.loadFeed();
                    this.loadMedia();
                }
                if (this.tripPhase === 'post') this.loadMedia();
            },
            onPostSignal: function() {
                this.loadAnnouncements();
            },
            onVisibility: function() {
                if (document.visibilityState === 'visible') this.loadAll();
            }
        },
        watch: {
            ready: function() {
                this.loadAll();
            }
        },
        mounted() {
            // App.vue owns socket connect/join/disconnect; this view only
            // subscribes to change signals.
            this.loadAll();
            socket.on('connect', this.loadAll);
            socket.on('post:new', this.onPostNew);
            socket.on('post:changed', this.onPostSignal);
            socket.on('vote:changed', this.onPostSignal);
            socket.on('reaction:changed', this.onPostSignal);
            socket.on('comment:changed', this.onPostSignal);
            document.addEventListener('visibilitychange', this.onVisibility);
        },
        beforeDestroy() {
            socket.off('connect', this.loadAll);
            socket.off('post:new', this.onPostNew);
            socket.off('post:changed', this.onPostSignal);
            socket.off('vote:changed', this.onPostSignal);
            socket.off('reaction:changed', this.onPostSignal);
            socket.off('comment:changed', this.onPostSignal);
            document.removeEventListener('visibilitychange', this.onVisibility);
        }
    };
</script>

<style lang="scss">
    .flip-card__top,
    .flip-card__bottom,
    .flip-card__back-bottom,
    .flip-card__back::before,
    .flip-card__back::after {
        color: #9ec43e !important;
    }
</style>
